import {
  ACESFilmicToneMapping,
  AmbientLight,
  Clock,
  Color,
  DirectionalLight,
  FogExp2,
  GridHelper,
  HemisphereLight,
  MOUSE,
  PerspectiveCamera,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import boardComponents from '../data/board-components.json';
import { AutoDemoController, type DemoStep } from '../animation/AutoDemoController';
import { SwitchSound } from '../audio/SwitchSound';
import { initialState, makeState } from '../circuit/LogicEvaluator';
import { FocusController } from '../interaction/FocusController';
import { inspectObjects, type InspectedObject } from '../model/BoardObjectInspector';
import { loadBoard } from '../model/BoardModelLoader';
import type { BoardComponent, RegionId, StateMap } from '../types';
import { REGIONS } from '../types';
import { HUD } from '../ui/HUD';
import { createLEDOverlay, setLEDState, type LEDVisual } from '../visualization/LEDGlowOverlay';
import {
  animateSwitch,
  createSwitchOverlay,
  isSwitchHit,
  setSwitchHover,
  setSwitchState,
  type SwitchVisual,
} from '../visualization/InteractiveSwitchOverlay';
import { createRegionHighlight, setRegionActive } from '../visualization/RegionHighlight';
import { SignalFlowOverlay } from '../visualization/SignalFlowOverlay';

export class LogicTeachingApp {
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(38, 1, 0.001, 10);
  private readonly renderer: WebGLRenderer;
  private readonly controls: OrbitControls;
  private readonly focus: FocusController;
  private readonly clock = new Clock();
  private readonly raycaster = new Raycaster();
  private readonly pointer = new Vector2();
  private readonly hud: HUD;
  private readonly mobile = matchMedia('(max-width: 760px), (pointer: coarse)').matches;
  private readonly states: StateMap = initialState();
  private readonly switches: SwitchVisual[] = [];
  private readonly leds = new Map<RegionId, LEDVisual>();
  private readonly highlights = new Map<RegionId, ReturnType<typeof createRegionHighlight>>();
  private readonly signalFlow = new SignalFlowOverlay(REGIONS, this.mobile);
  private readonly demo: AutoDemoController;
  private readonly switchSound = new SwitchSound();
  private active: RegionId = 'AND';
  private hovered: SwitchVisual | null = null;
  private debugOpen = false;
  private inspected: InspectedObject[] = [];
  private pointerDown = new Vector2();

  constructor(private readonly host: HTMLElement) {
    this.renderer = new WebGLRenderer({ antialias: !this.mobile, alpha: false, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, this.mobile ? 1.35 : 2));
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.shadowMap.enabled = !this.mobile;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;
    this.renderer.domElement.id = 'scene-canvas';
    this.host.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.065;
    this.controls.mouseButtons.LEFT = MOUSE.ROTATE;
    this.controls.mouseButtons.MIDDLE = MOUSE.PAN;
    this.controls.mouseButtons.RIGHT = MOUSE.PAN;
    this.controls.minDistance = 0.075;
    this.controls.maxDistance = 0.55;
    this.controls.maxPolarAngle = Math.PI * 0.49;
    this.focus = new FocusController(this.camera, this.controls);
    this.focus.snapHome();

    this.hud = new HUD(host, {
      selectRegion: (region) => this.selectRegion(region),
      resetView: () => this.resetView(),
      startDemo: () => this.demo.start(),
      pauseDemo: () => {
        this.demo.togglePause();
        this.hud.setStatus(this.demo.paused ? '自动演示已暂停' : '自动演示继续');
      },
      resetDemo: () => {
        this.demo.reset();
        this.resetStates();
      },
      toggleDebug: () => this.toggleDebug(),
    });
    this.demo = new AutoDemoController((step, index, total) => this.applyDemoStep(step, index, total));
    this.setupScene();
    this.bindEvents();
  }

  async start(): Promise<void> {
    try {
      const modelUrl = `${import.meta.env.BASE_URL}models/Logic_Gates_Teaching_Board_Final.glb`;
      const board = await loadBoard(modelUrl);
      this.scene.add(board.root);
      this.inspected = inspectObjects(board.root);
      this.createOverlays();
      this.hud.setDebug(this.inspected, `${boardComponents.length} KiCad footprints mapped`);
      this.updateVisuals();
      this.selectRegion('AND', false);
      this.hud.setReady();
      this.animate();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.hud.setLoadingError(message);
      throw error;
    }
  }

  private setupScene(): void {
    this.scene.background = new Color(0x06101d);
    this.scene.fog = new FogExp2(0x06101d, 2.7);
    this.scene.add(new HemisphereLight(0xa8d8ff, 0x111723, 2.3));
    this.scene.add(new AmbientLight(0xffffff, 0.45));
    const key = new DirectionalLight(0xe4f4ff, 3.2);
    key.position.set(-0.12, 0.28, 0.14);
    key.castShadow = !this.mobile;
    this.scene.add(key);
    const rim = new DirectionalLight(0x3d74ff, 2);
    rim.position.set(0.18, 0.11, -0.12);
    this.scene.add(rim);
    const grid = new GridHelper(0.7, 28, 0x183455, 0x0c2036);
    grid.position.y = -0.007;
    (grid.material as { opacity: number; transparent: boolean }).opacity = 0.3;
    (grid.material as { opacity: number; transparent: boolean }).transparent = true;
    this.scene.add(grid, this.signalFlow.root);
  }

  private createOverlays(): void {
    const components = boardComponents as BoardComponent[];
    for (const region of REGIONS) {
      region.switches.forEach((ref, index) => {
        const component = components.find((item) => item.ref === ref);
        if (!component) return;
        const visual = createSwitchOverlay(component, region.id, index === 0 ? 'a' : 'b');
        this.switches.push(visual);
        this.scene.add(visual.root);
      });
      const ledComponent = components.find((item) => item.ref === region.led);
      if (ledComponent) {
        const led = createLEDOverlay(ledComponent, region.id);
        this.leds.set(region.id, led);
        this.scene.add(led.root);
      }
      const highlight = createRegionHighlight(region);
      this.highlights.set(region.id, highlight);
      this.scene.add(highlight);
    }
  }

  private bindEvents(): void {
    addEventListener('resize', () => this.resize());
    addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.resetView();
    });
    const canvas = this.renderer.domElement;
    canvas.addEventListener('pointerdown', (event) => this.pointerDown.set(event.clientX, event.clientY));
    canvas.addEventListener('pointermove', (event) => this.onPointerMove(event));
    canvas.addEventListener('pointerup', (event) => this.onPointerUp(event));
    canvas.addEventListener('dblclick', () => this.resetView());
  }

  private onPointerMove(event: PointerEvent): void {
    const hits = this.pick(event);
    const hit = hits.find((object) => isSwitchHit(object));
    const next = hit ? this.switches.find((item) => item.ref === hit.userData.ref) ?? null : null;
    if (next !== this.hovered) {
      if (this.hovered) setSwitchHover(this.hovered, false);
      if (next) setSwitchHover(next, true);
      this.hovered = next;
      this.renderer.domElement.style.cursor = next ? 'pointer' : 'grab';
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (this.pointerDown.distanceTo(new Vector2(event.clientX, event.clientY)) > 8) return;
    const hits = this.pick(event);
    // The imported KiCad geometry can be closer to the camera than the
    // transparent interaction volume. Always prefer a switch hit anywhere
    // in the raycast results so the visible GLB cannot swallow the click.
    const switchHit = hits.find((object) => isSwitchHit(object));
    if (switchHit) {
      this.hud.setDebugHit(`命中开关：${switchHit.userData.ref}`);
      const visual = this.switches.find((item) => item.ref === switchHit.userData.ref);
      if (visual) this.toggleSwitch(visual);
      return;
    }
    const object = hits[0];
    if (!object) return;
    this.hud.setDebugHit(`命中：${object.name || object.parent?.name || object.type} · ${object.position.toArray().map((n) => n.toFixed(4)).join(', ')}`);
    const regionHit = hits.find((item) => item.userData.region);
    const region = regionHit?.userData.region as RegionId | undefined;
    if (region) this.selectRegion(region);
  }

  private pick(event: PointerEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    return this.raycaster.intersectObjects(this.scene.children, true).map((hit) => hit.object);
  }

  private toggleSwitch(visual: SwitchVisual): void {
    const state = this.states[visual.region];
    const a = visual.input === 'a' ? !state.a : state.a;
    const b = visual.input === 'b' ? !state.b : state.b;
    this.states[visual.region] = makeState(visual.region, a, b);
    void this.switchSound.play(visual.input === 'a' ? a : b);
    // Manual switch operation must preserve the learner's current camera.
    // Region-tab selection and auto-demo may focus the camera; a switch click
    // only changes circuit state and the information panel.
    this.selectRegion(visual.region, false);
    this.updateVisuals();
    this.hud.setStatus(`${visual.ref} → ${visual.input.toUpperCase()}=${+(visual.input === 'a' ? a : b)} · 输出 Y=${+this.states[visual.region].output}`);
  }

  private selectRegion(region: RegionId, moveCamera = true): void {
    this.active = region;
    const definition = REGIONS.find((item) => item.id === region)!;
    if (moveCamera) this.focus.focus(new Vector3(...definition.center));
    this.highlights.forEach((root, id) => setRegionActive(root, id === region));
    this.hud.update(this.states, region);
  }

  private updateVisuals(): void {
    for (const visual of this.switches) setSwitchState(visual, this.states[visual.region][visual.input]);
    this.leds.forEach((led, region) => setLEDState(led, this.states[region].output));
    for (const region of REGIONS) this.signalFlow.setState(region.id, this.states[region.id].output);
    this.hud.update(this.states, this.active);
  }

  private applyDemoStep(step: DemoStep, index: number, total: number): void {
    this.states[step.region] = makeState(step.region, step.a, step.b);
    this.selectRegion(step.region);
    this.updateVisuals();
    this.hud.setStatus(`自动演示 ${index + 1}/${total} · ${step.region}: A=${+step.a}${step.region === 'NOT' ? '' : `, B=${+step.b}`} → Y=${+this.states[step.region].output}`);
  }

  private resetStates(): void {
    const fresh = initialState();
    for (const region of REGIONS) this.states[region.id] = fresh[region.id];
    this.updateVisuals();
    this.hud.setStatus('实验状态已重置');
  }

  private resetView(): void {
    this.focus.reset();
    this.highlights.forEach((root) => setRegionActive(root, false));
    this.hud.setStatus('已恢复整板视角');
  }

  private toggleDebug(): void {
    this.debugOpen = !this.debugOpen;
    this.hud.showDebug(this.debugOpen);
  }

  private resize(): void {
    this.camera.aspect = innerWidth / innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(innerWidth, innerHeight);
  }

  private animate = (): void => {
    requestAnimationFrame(this.animate);
    const delta = Math.min(this.clock.getDelta(), 0.05);
    const elapsed = this.clock.elapsedTime;
    this.controls.update();
    this.focus.update(delta);
    this.demo.update(delta);
    this.signalFlow.update(elapsed);
    for (const item of this.switches) animateSwitch(item, delta);
    this.renderer.render(this.scene, this.camera);
  };
}
